import React, { useState } from "react";
import { CSVReader } from "react-papaparse";
import { Grid, Button, Message } from "semantic-ui-react";

function FileSelection({ customClassName, nextStepHandler }) {
  const requiredFields = {
    site: [
      "Site Type",
      ["Name", "Site Name"],
      "Site Geohash",
      "Lat",
      "Lon",
      "Altitude",
      "Status",
    ],
    node: [
      "Status",
      "Site Geohash",
      "Site Type",
      "Azimuth Orientation",
      "Sector Position",
      "Polarity",
    ],
    link: [
      "Link Geohash",
      "Status",
      ["Length", "Distance"],
      "Sector Positions",
      "Link Type",
    ],
  };
  const [data, setData] = useState({ site: [], link: [], node: [] });
  const [fileUploaded, setFileUploaded] = useState({
    site: false,
    link: false,
    node: false,
  });
  const [errors, setErrors] = useState({ site: null, link: null, node: null });
  const handleOnDrop = (fileData, type) => {
    console.log("filedata", fileData);
    if (type === "node") {
      fileData = fileData.filter(
        (obj) =>
          (obj.data.Status === "Proposed" || obj.data.Status === "PROPOSED") &&
          (obj["data"]["Site Type"] === "DN" ||
            obj["data"]["Site Type"] === "POP")
      );
    } else if (type === "link") {
      fileData = fileData.filter(
        (obj) =>
          obj.data.Status === "Proposed" || obj.data.Status === "PROPOSED"
      );
    }
    console.log("after filter", fileData.length);
    if (fileData.length) {
      const fields = Object.keys(fileData[0]["data"]);
      const reqFieldExists = requiredFields[type].every((field) => {
        if (Array.isArray(field)) {
          return field.some((name) => fields.includes(name));
        } else {
          return fields.includes(field);
        }
      });
      if (reqFieldExists) {
        setData({ ...data, [type]: fileData });
        setFileUploaded({ ...fileUploaded, [type]: true });
      } else {
        setErrors({
          ...errors,
          [type]: "Required fields are missing or Incorrect file",
        });
      }
    } else {
      setErrors({
        ...errors,
        [type]: "Required fields are missing or Incorrect file",
      });
    }
  };

  const handleOnError = (data, type) => {
    setErrors({ ...errors, [type]: `Failed to parse ${type} file` });
  };

  const handleOnRemove = (type) => {
    setData({ ...data, [type]: [] });
    setFileUploaded({ ...fileUploaded, [type]: false });
    setErrors({ ...errors, [type]: null });
  };
  const fileTypes = ["link", "node", "site"];

  const isValid = () => {
    const allFilesUploaded = Object.values(fileUploaded).every(
      (value) => value
    );
    const validData = Object.values(errors).every((value) => value === null);
    return allFilesUploaded && validData;
  };

  const cnProductDetails = {
    V3000: {
      antennaId: "2a8d8829-e11d-417b-823f-1600833c3c5f",
      productName: "V603000_PMP",
    },
    V1000: {
      antennaId: "6b5f7168-8d8c-4862-91a7-ac86787451f8",
      productName: "V601000_PMP",
    },
  };
  const processData = () => {
    const cnSites = {},
      dnSites = {},
      nodes = {},
      links = {},
      meshLinks = {},
      meshLinksCreated = [],
      //siteNames = [];
      cnSiteNames = data.site
        .filter((obj) => obj["data"]["Site Type"] === "CN")
        .map(
          (site) =>
            site["data"]["Name"] ||
            site["data"]["Site Name"] ||
            site["data"]["Site Geohash"]
        ),
      dnSiteNames = data.site
        .filter(
          (obj) =>
            obj["data"]["Site Type"] === "DN" ||
            obj["data"]["Site Type"] === "POP"
        )
        .map(
          (site) =>
            site["data"]["Name"] ||
            site["data"]["Site Name"] ||
            site["data"]["Site Geohash"]
        );
    let cnSiteCount = 0,
      apId = 0,
      dnSiteCount = 0;
    data.site.forEach((site) => {
      const data = site.data;
      const geohash = data["Site Geohash"];
      let name = data["Name"] || data["Site Name"] || geohash;
      const siteObject = {
        lat: data["Lat"],
        lng: data["Lon"],
        altitude: data["Altitude"],
        status: data["Status"],
        deviceSku: data["Device SKU"],
      };
      if (data["Site Type"] === "DN" || data["Site Type"] === "POP") {
        const sameNameCount = countInArray(dnSiteNames, name);
        if (sameNameCount > 1) {
          name = `${data["Name"] || data["Site Name"]} - ${geohash}`;
        }
        siteObject["name"] = name;
        siteObject["placeId"] = dnSiteCount++;
        dnSites[geohash] = siteObject;
      } else {
        const sameNameCount = countInArray(cnSiteNames, name);
        if (sameNameCount > 1) {
          name = `${data["Name"] || data["Site Name"]} - ${geohash}`;
        }
        name = name.replace(/_v3000|_v1000/, "");
        siteObject["name"] = name;
        siteObject["placeId"] = cnSiteCount++;
        const productType = ["V3000", "v3000"].includes(siteObject["deviceSku"])
          ? "V3000"
          : "V1000";
        siteObject["antennaId"] = cnProductDetails[productType].antennaId;
        siteObject["productName"] = cnProductDetails[productType].productName;
        if (cnSites[geohash]) {
          cnSites[geohash].push(siteObject);
        } else {
          cnSites[geohash] = [siteObject];
        }
      }
    });

    // Multiple sites at same geohash so filter them
    // based on PROPOSED status or device SKU as v1000
    const updateCnSites = {};
    for (const [key, value] of Object.entries(cnSites)) {
      if (value.length == 1) {
        updateCnSites[key] = value[0];
      } else {
        const proposedSite = value.find((site) =>
          ["PROPOSED", "Proposed"].includes(site.status)
        );
        if (proposedSite) {
          updateCnSites[key] = proposedSite;
        } else {
          const v1000Site = value.find((site) =>
            ["V1000", "v1000"].includes(site["deviceSku"])
          );
          updateCnSites[key] = v1000Site;
        }
      }
    }
    data.node.forEach((node) => {
      const data = node["data"];
      const siteGeohash = data["Site Geohash"];
      const nodeObject = {
        azimuth: data["Azimuth Orientation"],
        sectorPosition: data["Sector Position"],
        polarity: data["Polarity"],
      };
      if (nodes[siteGeohash] == null) {
        nodes[siteGeohash] = {
          accessPoint: {
            apId: apId++,
            antennaHeight: 10,
            antenna: "d289296b-f730-41a4-b2c0-0410fb26d76d",
            smRange: 0, // need to update
            azimuth: 0, // need to update
          },
          sectors: [nodeObject],
        };
      } else {
        nodes[siteGeohash]["sectors"].push(nodeObject);
      }
    });

    data.link.forEach((link) => {
      const data = link["data"];
      const geohash = data["Link Geohash"];
      const geohashArr = geohash.split("-");
      let sectorPosition = data["Sector Positions"];
      if (sectorPosition != null) {
        sectorPosition = sectorPosition.split("-->")[0] || 0;
        sectorPosition = parseInt(sectorPosition) + 1;
      }
      const linkObj = {
        //txSite: data["Tx Site Name"],
        //rxSite: data["Rx Site Name"],
        length: data["Length"] || data["Distance"],
        sectorPosition,
      };
      let localGeohash = "",
        remoteGeohash = "";
      /* CN_DN WA
        DN_CN WA
        POP_CN WA
        CN_POP WA
        POP_DN WB
        DN_POP WB
        DN_DN WB*/

      if (
        geohashArr[0].indexOf("DN") > -1 ||
        geohashArr[0].indexOf("POP") > -1
      ) {
        localGeohash = geohashArr[0].split("_")[0];
        remoteGeohash = geohashArr[1].split("_")[0];
      } else {
        localGeohash = geohashArr[1].split("_")[0];
        remoteGeohash = geohashArr[0].split("_")[0];
      }
      linkObj["remoteGeohash"] = remoteGeohash;
      if (data["Link Type"] === "WIRELESS_ACCESS") {
        if (links[localGeohash] == null) {
          links[localGeohash] = [linkObj];
        } else {
          links[localGeohash].push(linkObj);
        }
      } else if (data["Link Type"] === "WIRELESS_BACKHAUL") {
        //WIRELESS_BACKHAUL
        let dataFlow = data["Data flow (Gbps)"];
        dataFlow = parseFloat(dataFlow);
        dataFlow = dataFlow * 1000;
        dataFlow += 10;
        linkObj["dataFlow"] = dataFlow;
        const linkId = [localGeohash, remoteGeohash].sort().join();
        if (meshLinksCreated.includes(linkId)) {
          const foundLink = meshLinks[localGeohash].filter(
            (link) => link["remoteGeohash"] === remoteGeohash
          )[0];
          if (foundLink && linkObj["dataFlow"] > foundLink["dataFlow"]) {
            foundLink["dataFlow"] = linkObj["dataFlow"];
          }
        } else {
          if (meshLinks[localGeohash] == null) {
            meshLinks[localGeohash] = [linkObj];
          } else {
            meshLinks[localGeohash].push(linkObj);
          }
          meshLinksCreated.push(linkId);
        }
      }
    });

    //update smRange and azimuth
    Object.entries(nodes).forEach(([geohash, node]) => {
      const { accessPoint, sectors } = node;
      if (sectors.length == 1) {
        node.sectors[1] = { ...sectors[0] };
        node.sectors[1].sectorPosition = "1";
      }
      const sectorZero = sectors.filter(
        (sector) => sector.sectorPosition == 0
      )[0];
      if (sectorZero != null) {
        accessPoint.azimuth = (parseFloat(sectorZero.azimuth) + 70 + 360) % 360;
      } else {
        accessPoint.azimuth = (parseFloat(sectors[0].azimuth) - 70 + 360) % 360;
      }
      let linksOrMeshLinks = links[geohash];
      if (!links[geohash]) {
        linksOrMeshLinks = meshLinks[geohash];
      }
      if (linksOrMeshLinks) {
        let linkLengths = linksOrMeshLinks.map((link) =>
          parseFloat(link.length)
        );
        linkLengths = linkLengths.sort((a, b) => a - b);
        let farthestLink = linkLengths.pop();
        farthestLink += 1; // Adding 1 to fix rounding issues between ANP and LP
        farthestLink = farthestLink / 1000; // convert to km
        accessPoint.smRange = Math.ceil(farthestLink * 100) / 100;
      }
    });
    console.log(
      "cnSites",
      cnSites,
      "updatecnSites",
      updateCnSites,
      "dnSites",
      dnSites,
      "nodes",
      nodes,
      "links",
      links,
      "meshLinks",
      meshLinks
    );
    return {
      cnSites: updateCnSites,
      dnSites,
      nodes,
      links,
      meshLinks,
      dateCreated: new Date(),
    };
  };

  return (
    <>
      <Grid className={customClassName}>
        {fileTypes.map((type) => {
          return (
            <Grid.Row>
              <Grid.Column width={8} className="file-drop-container">
                <CSVReader
                  onDrop={(data) => handleOnDrop(data, type)}
                  onError={handleOnError}
                  noClick
                  addRemoveButton
                  onRemoveFile={() => handleOnRemove(type)}
                  config={{ header: true, skipEmptyLines: true }}
                >
                  <span>Drop {type} file here to upload.</span>
                </CSVReader>
              </Grid.Column>
              <Grid.Column width={8} verticalAlign="middle">
                {errors[type] != null && (
                  <Message negative>
                    <p>{errors[type]}</p>
                  </Message>
                )}
                {errors[type] == null && data[type].length > 0 && (
                  <Button circular icon="check" color="green" />
                )}
              </Grid.Column>
            </Grid.Row>
          );
        })}

        <Grid.Row columns={1}>
          <Grid.Column
            floated="right"
            width={2}
            style={{ paddingLeft: "54px" }}
          >
            <div className="fixed-button">
              <Button
                disabled={!isValid()}
                onClick={() => {
                  const data = processData();
                  nextStepHandler(data, "filedata");
                }}
              >
                Next
              </Button>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
}

export default FileSelection;
function countInArray(array, value) {
  return array.reduce((n, x) => n + (x === value), 0);
}
